import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Percent, Save, Cake, Crown, CheckCircle2, Loader2, ExternalLink, Sparkles, Receipt } from "lucide-react";
import BillingInfoModal from "@/components/BillingInfoModal";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [markup, setMarkup] = useState(user?.markup_pct || 30);
  const [savingMarkup, setSavingMarkup] = useState(false);
  const [cake, setCake] = useState({ base: 250, superior: 500, luxury: 1000 });
  const [savingCake, setSavingCake] = useState(false);

  const [billing, setBilling] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [billingInfoModalPlan, setBillingInfoModalPlan] = useState(null);
  const [showBillingInfoEditor, setShowBillingInfoEditor] = useState(false);
  const [billingInfoSummary, setBillingInfoSummary] = useState(null); // {complete, billing_info}

  useEffect(() => {
    setMarkup(user?.markup_pct ?? 30);
    if (user?.cake_prices) setCake({ ...cake, ...user.cake_prices });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadBilling = async () => {
    setLoadingPlans(true);
    try {
      const r = await api.get("/billing/plans");
      setBilling(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPlans(false);
    }
  };
  useEffect(() => { loadBilling(); }, []);

  // Handle return from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("stripe_session_id");
    const cancelled = params.get("stripe_cancelled");
    if (cancelled) {
      toast.error("Checkout annullato");
      window.history.replaceState({}, "", "/impostazioni");
      return;
    }
    if (!sid) return;
    let cancel = false;
    let attempts = 0;
    const poll = async () => {
      if (cancel) return;
      try {
        const r = await api.get(`/billing/checkout-status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success("Abbonamento attivato!");
          window.history.replaceState({}, "", "/impostazioni");
          await loadBilling();
          return;
        }
        if (r.data.status === "expired") {
          toast.error("Sessione scaduta");
          window.history.replaceState({}, "", "/impostazioni");
          return;
        }
        if (attempts < 10) {
          attempts++;
          setTimeout(poll, 2000);
        }
      } catch { /* ignore */ }
    };
    toast.loading("Verifica pagamento in corso...", { id: "poll" });
    poll().finally(() => toast.dismiss("poll"));
    return () => { cancel = true; };
  }, []);

  const startCheckout = async (planKey) => {
    setCheckoutPlan(planKey);
    try {
      const r = await api.post("/billing/checkout", { plan: planKey, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 412 || detail?.code === "billing_info_required") {
        setCheckoutPlan(null);
        setBillingInfoModalPlan(planKey);
        return;
      }
      toast.error(typeof detail === "string" ? detail : detail?.message || "Errore creazione checkout");
      setCheckoutPlan(null);
    }
  };

  const onBillingInfoSaved = () => {
    const plan = billingInfoModalPlan;
    setBillingInfoModalPlan(null);
    loadBillingInfoSummary();
    if (plan) startCheckout(plan);
  };

  const loadBillingInfoSummary = async () => {
    try {
      const r = await api.get("/billing/info");
      setBillingInfoSummary(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadBillingInfoSummary(); }, []);

  const cancelSub = async () => {
    if (!window.confirm("Annullare l'abbonamento? Resterà attivo fino alla fine del periodo corrente.")) return;
    try {
      await api.post("/billing/cancel");
      toast.success("Abbonamento programmato per cancellazione");
      await loadBilling();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const resumeSub = async () => {
    try {
      await api.post("/billing/resume");
      toast.success("Cancellazione annullata");
      await loadBilling();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const saveMarkup = async () => {
    setSavingMarkup(true);
    try {
      await api.put("/auth/markup", { markup_pct: parseFloat(markup) });
      await refresh();
      toast.success("Ricarico aggiornato");
    } catch { toast.error("Errore"); }
    finally { setSavingMarkup(false); }
  };

  const saveCake = async () => {
    setSavingCake(true);
    try {
      await api.put("/auth/cake-prices", cake);
      await refresh();
      toast.success("Prezzi torte aggiornati");
    } catch { toast.error("Errore"); }
    finally { setSavingCake(false); }
  };

  const status = billing?.status;
  const currentPlan = status?.plan || "free";

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-[#2563EB] font-semibold font-ui">Profilo</p>
        <h1 className="font-display text-4xl md:text-5xl text-[#0F2741] tracking-tight mt-2">Impostazioni</h1>
      </div>

      {/* Dati di fatturazione */}
      <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="billing-info-section">
        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[#2563EB]" />
            <h2 className="font-ui font-semibold text-lg text-[#0F2741]">Dati di fatturazione</h2>
            {billingInfoSummary?.complete ? (
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Completi</span>
            ) : (
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Da completare</span>
            )}
          </div>
          <button onClick={() => setShowBillingInfoEditor(true)} className="btn-ghost border border-slate-200" data-testid="btn-edit-billing-info">
            {billingInfoSummary?.complete ? "Modifica" : "Inserisci dati"}
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-3">Necessari per attivare un piano a pagamento ed emettere fattura elettronica.</p>
        {billingInfoSummary?.complete && billingInfoSummary?.billing_info ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><span className="text-xs text-slate-500 uppercase tracking-wider">Ragione sociale</span><div className="font-semibold text-slate-800">{billingInfoSummary.billing_info.company_name}</div></div>
            <div><span className="text-xs text-slate-500 uppercase tracking-wider">P.IVA / CF</span><div className="font-semibold text-slate-800">{billingInfoSummary.billing_info.vat_or_cf}</div></div>
            <div className="sm:col-span-2"><span className="text-xs text-slate-500 uppercase tracking-wider">Indirizzo</span><div className="font-semibold text-slate-800">{billingInfoSummary.billing_info.address}</div></div>
            <div><span className="text-xs text-slate-500 uppercase tracking-wider">Email fattura</span><div className="font-semibold text-slate-800">{billingInfoSummary.billing_info.billing_email}</div></div>
            <div><span className="text-xs text-slate-500 uppercase tracking-wider">PEC / SDI</span><div className="font-semibold text-slate-800">{billingInfoSummary.billing_info.pec_or_sdi}</div></div>
            <div><span className="text-xs text-slate-500 uppercase tracking-wider">Telefono</span><div className="font-semibold text-slate-800">{billingInfoSummary.billing_info.phone}</div></div>
          </div>
        ) : (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            ⚠️ Prima di sottoscrivere un piano a pagamento (al termine del periodo di prova) è obbligatorio inserire i dati di fatturazione.
          </p>
        )}
      </div>

      {/* Subscription */}
      <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="subscription-section">
        <div className="flex items-center gap-2 mb-1">
          <Crown className="w-5 h-5 text-[#2563EB]" />
          <h2 className="font-ui font-semibold text-lg text-[#0F2741]">Abbonamento</h2>
        </div>
        <p className="text-sm text-slate-500 mb-5">Scegli il piano che meglio si adatta alla tua attività.</p>

        {loadingPlans ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento piani...</div>
        ) : (
          <>
            {/* Status banner */}
            <div className={`mb-5 p-4 rounded-xl border ${
              currentPlan === "premium" ? "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300" :
              currentPlan === "base" ? "bg-[#EFF6FF] border-[#2563EB]/30" :
              "bg-slate-50 border-slate-200"
            }`} data-testid="current-plan-banner">
              <div className="flex justify-between items-start flex-wrap gap-2">
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Piano attuale</p>
                  <p className="font-display text-2xl text-[#0F2741] mt-1 capitalize">
                    {currentPlan === "free" ? "Trial gratuito" : currentPlan}
                    {status?.source === "whitelist" && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-200 text-amber-800 align-middle">VIP</span>}
                  </p>
                  {currentPlan === "free" && (
                    <p className="text-sm text-slate-600 mt-1">
                      Hai usato <strong>{status.events_used}</strong> {status.events_used === 1 ? "evento" : "eventi"} su <strong>{status.free_quota}</strong> gratuiti.
                      {status.events_used >= status.free_quota && <span className="text-rose-600 font-semibold"> Quota esaurita.</span>}
                    </p>
                  )}
                  {currentPlan !== "free" && status?.current_period_end && (
                    <p className="text-sm text-slate-600 mt-1">
                      {status.cancel_at_period_end ? "Cessazione il" : "Si rinnova il"} <strong>{new Date(status.current_period_end).toLocaleDateString("it-IT")}</strong>
                    </p>
                  )}
                </div>
                {status?.source === "stripe" && (
                  <div className="flex gap-2">
                    {status.cancel_at_period_end ? (
                      <button onClick={resumeSub} className="btn-ghost border border-slate-200 text-sm">Ripristina</button>
                    ) : (
                      <button onClick={cancelSub} data-testid="btn-cancel-sub" className="btn-ghost border border-rose-200 text-rose-600 hover:bg-rose-50 text-sm">Annulla abbonamento</button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Plan cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(billing?.plans || []).map((p) => {
                const isCurrent = currentPlan === p.key && status?.source !== "trial";
                const isPremium = p.key === "premium";
                return (
                  <div
                    key={p.key}
                    data-testid={`plan-card-${p.key}`}
                    className={`p-6 rounded-xl border-2 relative ${
                      isPremium ? "border-amber-400 bg-gradient-to-br from-white to-amber-50" : "border-slate-200 bg-white"
                    } ${isCurrent ? "ring-2 ring-emerald-400" : ""}`}
                  >
                    {isPremium && (
                      <span className="absolute -top-3 right-4 text-xs px-3 py-0.5 rounded-full bg-amber-400 text-amber-900 font-bold uppercase tracking-wider">Consigliato</span>
                    )}
                    <div className="flex items-center gap-2">
                      {isPremium ? <Crown className="w-5 h-5 text-amber-500" /> : <Sparkles className="w-5 h-5 text-[#2563EB]" />}
                      <h3 className="font-display text-2xl text-[#0F2741]">{p.name}</h3>
                    </div>
                    <p className="mt-3">
                      <span className="font-display text-4xl text-[#0F2741]">€{p.price_eur.toFixed(2).replace(".", ",")}</span>
                      <span className="text-slate-500 text-sm">/mese</span>
                    </p>
                    <p className="text-sm text-slate-500 mt-2">{p.description}</p>
                    <ul className="mt-4 space-y-1.5">
                      {p.features.map((f, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => startCheckout(p.key)}
                      disabled={isCurrent || checkoutPlan === p.key}
                      data-testid={`btn-subscribe-${p.key}`}
                      className={`mt-5 w-full ${isPremium ? "btn-primary bg-amber-500 hover:bg-amber-600 border-amber-500" : "btn-primary"} ${isCurrent ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      {checkoutPlan === p.key ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Reindirizzamento...</>
                      ) : isCurrent ? (
                        <>Piano attivo</>
                      ) : (
                        <><ExternalLink className="w-4 h-4" /> Sottoscrivi {p.name}</>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-4 text-center">Pagamenti sicuri via Stripe · IVA inclusa · Cancellabile in qualsiasi momento</p>
          </>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-xl">
        <h2 className="font-ui font-semibold text-lg text-[#0F2741] mb-4">Ricarico standard sui preventivi</h2>
        <p className="text-sm text-slate-500 mb-4">Percentuale di markup applicata sul costo interno per calcolare il prezzo finale al cliente.</p>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1 mb-1"><Percent className="w-3 h-3" /> Ricarico (%)</label>
            <input data-testid="input-markup" type="number" min="0" step="1" value={markup} onChange={(e) => setMarkup(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#2563EB] outline-none text-lg font-semibold" />
          </div>
          <button onClick={saveMarkup} disabled={savingMarkup} data-testid="btn-save-markup" className="btn-primary"><Save className="w-4 h-4" /> Salva</button>
        </div>
        <p className="text-xs text-slate-400 mt-3">Esempio: con ricarico {markup}%, un evento da €1.000 di costo interno verrà preventivato a €{(1000 * (1 + markup / 100)).toFixed(0)}.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
        <div className="flex items-center gap-2 mb-2">
          <Cake className="w-5 h-5 text-[#2563EB]" />
          <h2 className="font-ui font-semibold text-lg text-[#0F2741]">Prezzi torte standard</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">Configura i prezzi default per le tre tipologie di torta. Saranno proposti automaticamente in ogni nuovo evento, ma il prezzo è sempre modificabile sul singolo evento.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {["base", "superior", "luxury"].map((tier) => (
            <div key={tier} className="p-4 rounded-xl border border-slate-200">
              <p className="font-display text-xl text-[#0F2741] capitalize">{tier}</p>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-slate-500 font-semibold">€</span>
                <input type="number" step="10" min="0" data-testid={`input-cake-${tier}`} value={cake[tier] ?? 0}
                  onChange={(e) => setCake({ ...cake, [tier]: parseFloat(e.target.value) || 0 })}
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-xl font-semibold focus:ring-2 focus:ring-[#2563EB] outline-none" />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={saveCake} disabled={savingCake} data-testid="btn-save-cake" className="btn-primary"><Save className="w-4 h-4" /> Salva prezzi torte</button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-xl">
        <h2 className="font-ui font-semibold text-lg text-[#0F2741] mb-4">Account</h2>
        <div className="space-y-2 text-sm">
          <div><span className="text-slate-500">Nome:</span> <strong className="text-[#0F2741]">{user?.name}</strong></div>
          <div><span className="text-slate-500">Email:</span> <strong className="text-[#0F2741]">{user?.email}</strong></div>
        </div>
      </div>

      {billingInfoModalPlan && (
        <BillingInfoModal
          planKey={billingInfoModalPlan}
          onClose={() => setBillingInfoModalPlan(null)}
          onSaved={onBillingInfoSaved}
        />
      )}
      {showBillingInfoEditor && (
        <BillingInfoModal
          planKey={null}
          onClose={() => setShowBillingInfoEditor(false)}
          onSaved={() => { setShowBillingInfoEditor(false); loadBillingInfoSummary(); }}
        />
      )}
    </div>
  );
}
